import { z } from "zod";
import { INDUSTRY_OPTIONS, TECHNOLOGY_OPTIONS } from "@kansai-ai-hub/types";
import { notificationEmailsSchema } from "./admin.js";
import { passwordSchema } from "./auth.js";
/**
 * 問い合わせ・申請の入出力契約。
 *
 * 2 つの流入経路がある:
 * - LP（kansaiaihub.com）からの server-to-server 転送（共有鍵認証）
 * - 本ポータルの公開申請フォーム（未認証のブラウザから同一オリジンで POST）
 *
 * どちらも「送信側のバリデーションは迂回されうる」前提でサーバー側でも全項目を検証する。
 */
export const inquiryKindEnum = z.enum([
    "CONTACT",
    "RESERVATION",
    "SEMINAR",
    "ENTERPRISE_APPLICATION",
    "MEMBER_APPLICATION",
]);
/**
 * 取り込み（LP からの転送）で受け付ける kind。
 *
 * LP のフォームは 3 種類（お問い合わせ / 検証環境のお問い合わせ / セミナー申込）あり、
 * いずれも事務局が管理画面で捌く。LP 側はメール送信を持たないため、この取り込みが
 * **唯一の配送経路**である。ここで拒否した kind は届いた瞬間に消える。
 *
 * ポータルの利用申込（ENTERPRISE_APPLICATION / MEMBER_APPLICATION）だけは LP 経由で
 * 発生しないため受け付けない（本ポータルの公開フォームから applicationSubmissionSchema で入る）。
 *
 * kind を増減するときは、①LP のプライバシーポリシー 3(4) の記述、②この enum、
 * ③管理画面のタブ（features/admin/inquiries/kinds.ts の INQUIRY_TABS）の 3 点を必ず揃えること。
 */
export const ingestableInquiryKindEnum = z.enum(["CONTACT", "RESERVATION", "SEMINAR"]);
/**
 * 対応状況。**LP 由来の問い合わせと利用申込で使う値が違う**（値集合は共通、
 * どの値を使えるか・どう遷移できるかは `inquiry-workflow.ts` の遷移表が kind ごとに決める）。
 * Prisma の `InquiryStatus` と 1 対 1。片方だけ増やすと型で気づけないので必ず両方直すこと。
 */
export const inquiryStatusEnum = z.enum([
    "NEW",
    "IN_PROGRESS",
    "DONE",
    "SPAM",
    "APPROVED",
    "REJECTED",
    "ACCOUNT_CREATED",
]);
/**
 * 各項目の上限は「実運用で通る長さ」より十分大きく、かつ DB とログを膨らませない値にする。
 * 本文だけは相談内容が長くなりうるため 1 万文字まで許容する。
 */
/**
 * 表示を偽装しうる不可視文字と、DB が受け付けない NUL を落とす。
 * - PostgreSQL の text 列は 0x00 を拒否するため、素通しすると insert が 500 になる
 * - Unicode 双方向制御は React のエスケープでは無害化されず、管理画面の表示を反転できる
 */
// eslint-disable-next-line no-control-regex
const UNSAFE_TEXT_CHARS = /[\x00-\x08\x0b\x0c\x0e-\x1f\x7f\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/g;
/**
 * 制御文字・双方向制御を落として trim する。改行とタブは本文で意味を持つので残す。
 *
 * zod の `.transform()` にはしない —— input/output 型が割れて react-hook-form の
 * resolver 型が合わなくなるため（createUserSchema と同じ制約）。
 * 呼ぶのはサーバー側（InquiriesService）。
 */
export function sanitizeInquiryText(value) {
    return value.replace(UNSAFE_TEXT_CHARS, "").trim();
}
/**
 * 種別固有項目（payload）の値をまとめて sanitize する。
 *
 * **取り込み口は共有鍵さえあれば任意の payload を送れる。** 本文や氏名だけを
 * 通していると、payload の値に NUL が 1 つ混ざっただけで `jsonb` が
 * `unsupported Unicode escape sequence` で拒否し、取り込みが 500 になる。
 * 500 は冪等キーに成功として記録されないので、LP がリトライしても毎回同じ結果になり、
 * **その問い合わせは永久に取り込めないまま消える**（LP 側には届いたように見える）。
 * 双方向制御文字も同じ経路で管理画面の表示に出てしまう。
 */
export function sanitizeInquiryPayload(payload) {
    /*
     * プロトタイプ無しの器に入れる。`__proto__` というキーが来たとき、
     * 素のオブジェクトへの代入は文字列なら黙って捨てられ、配列ならプロトタイプを差し替える。
     */
    const out = Object.create(null);
    for (const [key, value] of Object.entries(payload)) {
        const cleanKey = sanitizeInquiryText(key);
        if (cleanKey === "")
            continue;
        // 正規化で別々のキーが衝突しうる（"subject" と "subject\u200b"）。
        // 後勝ちにすると先に入っていた値が黙って消えるので、先勝ちにする。
        if (Object.hasOwn(out, cleanKey))
            continue;
        out[cleanKey] = Array.isArray(value)
            ? value.map((v) => sanitizeInquiryText(v))
            : sanitizeInquiryText(value);
    }
    // Prisma / JSON へ渡すので通常のオブジェクトに戻す。
    return { ...out };
}
const NAME_MAX = 200;
const EMAIL_MAX = 320; // RFC 5321 の実用上限
const BODY_MAX = 10_000;
/** 種別固有項目は「フォームの1入力欄」相当なので、本文ほどの長さは要らない。 */
const PAYLOAD_KEY_MAX = 64;
const PAYLOAD_VALUE_MAX = 2_000;
const PAYLOAD_MAX_KEYS = 30;
/** 配列値の要素数上限（通知先メール5件・業界/技術タグ程度を想定）。 */
const PAYLOAD_MAX_ARRAY_ITEMS = 20;
/** 種別固有項目の合計。本文（10,000）より小さく保つ。 */
const PAYLOAD_TOTAL_MAX = 8_000;
/**
 * 種別固有項目の値。文字列に加えて文字列配列を許可する
 * （得意業界・得意技術・通知先メールのような複数値を、区切り文字で潰さずに保持するため）。
 * ネストしたオブジェクトは許可しない（管理画面の表示が複雑になり、サイズも制御できなくなる）。
 */
const payloadValueSchema = z.union([
    z.string().max(PAYLOAD_VALUE_MAX),
    z.array(z.string().max(PAYLOAD_VALUE_MAX)).max(PAYLOAD_MAX_ARRAY_ITEMS),
]);
function payloadTotalLength(p) {
    return Object.entries(p).reduce((n, [k, v]) => n + k.length + (Array.isArray(v) ? v.reduce((m, s) => m + s.length, 0) : v.length), 0);
}
/**
 * 種別固有の項目（inquiryType / subject / industries 等）。
 *
 * キー数・キー長・値長・合計長すべてに上限を置く。ここが無制限だと、鍵が漏れた場合や
 * 公開フォームへの自動投稿で、リクエストボディ上限いっぱいまで JSONB を注入でき、
 * 管理画面の詳細表示（payload を全件描画する）も巻き添えで重くなる。
 */
export const inquiryPayloadSchema = z
    .record(z.string().min(1).max(PAYLOAD_KEY_MAX), payloadValueSchema)
    .default({})
    .refine((p) => Object.keys(p).length <= PAYLOAD_MAX_KEYS, {
    message: `payload のキーは ${PAYLOAD_MAX_KEYS} 個までです`,
})
    // 個別上限だけだと 30 キー x 2,000 文字 = 60,000 文字となり、本文(10,000)より
    // 大きくなってしまう。合計にも上限を置いて 1 レコードの最大サイズを抑える。
    .refine((p) => payloadTotalLength(p) <= PAYLOAD_TOTAL_MAX, {
    message: `payload は合計 ${PAYLOAD_TOTAL_MAX} 文字までです`,
});
export const inquiryIngestSchema = z.object({
    kind: ingestableInquiryKindEnum,
    name: z.string().trim().min(1).max(NAME_MAX),
    /** contact フォームでは任意入力。空文字は未入力として扱う。 */
    organization: z
        .string()
        .trim()
        .max(NAME_MAX)
        .optional()
        .transform((v) => (v === "" ? undefined : v)),
    email: z.string().trim().email().max(EMAIL_MAX),
    body: z.string().trim().min(1).max(BODY_MAX),
    payload: inquiryPayloadSchema,
    /** LP のフォームが送信された時刻（ISO 8601）。 */
    submittedAt: z.string().datetime(),
});
// ────────────────────────────────────────────────────────────────────────────
// ポータルの利用申込（公開フォーム）
// ────────────────────────────────────────────────────────────────────────────
/** 申込種別。管理画面の「portal企業側申請 / portal会員側申請」タブに 1 対 1 で対応する。 */
export const applicationKindEnum = z.enum(["ENTERPRISE_APPLICATION", "MEMBER_APPLICATION"]);
const ORG_DESC_MAX = 1_000;
const HOMEPAGE_MAX = 500;
const NOTE_MAX = 2_000;
/**
 * 事務局が把握している紹介元。利用申込フォーム（企業・会員とも）の「推薦者」に対応する。
 * 必須項目なので、紹介元が無い人のために先頭に「なし」を置く。
 */
export const REFERRER_NONE = "なし";
export const REFERRER_OPTIONS = [
    REFERRER_NONE,
    "レノボ・ジャパン合同会社・レノボ・エンタープライズ・ソリューションズ合同会社",
    "インテル株式会社",
    "株式会社大阪取引所グループ",
    "Nakanoshima Qross（中之島クロス）",
    "JAM BASE",
    "その他",
];
const REFERRER_NAME_MAX = 100;
/**
 * 利用申込（企業・会員とも）の推薦者まわりの必須チェック。フロント（form-schema.ts）とサーバ
 * （applications.controller.ts）で同じ判定・文言を使うためにここへ置く。
 *
 * - 推薦者（団体）は必須。紹介元が無ければ「なし」を選ぶ。
 * - 推薦者の人名（referrerName）は任意。分かる場合だけ書いてもらう。
 *
 * 共有スキーマ側を `.superRefine` にすると ZodObject でなくなり、フロントの `.extend` が
 * 壊れるため、スキーマの外に関数として持つ（createUserSchema の currentPassword と同じ方針）。
 */
export function findReferrerIssues(input) {
    if (!input.referrer)
        return [{ path: "referrer", message: "推薦者を選択してください" }];
    return [];
}
/**
 * 任意テキスト。`.transform()` は使わない —— input/output 型が割れて react-hook-form の
 * resolver 型が合わなくなるため（createUserSchema と同じ方針）。
 * 空文字の「未入力」への正規化はサーバー側（InquiriesService）で行う。
 */
const optionalText = (max) => z.string().trim().max(max).optional();
/**
 * 利用申込フォームの送信内容。
 *
 * 項目は管理画面の「企業追加 / 会員追加」（features/admin/*-register-page.tsx が使う
 * `createUserSchema`）と揃える。申込者が希望するログインパスワードもここで受け取り、
 * サーバー側で argon2 ハッシュにして保存する（平文は保持せず、API でも一切返さない）。
 * 承認してアカウントを作成する際に、そのハッシュを User にそのまま移す。
 *
 * パスワード確認（confirmPassword）はフロント専用なのでこのスキーマには含めない
 * （createUserSchema / registerFormSchema と同じ分担）。
 *
 * 得意業界・得意技術は `INDUSTRY_OPTIONS` / `TECHNOLOGY_OPTIONS` を正とし、複数選択で受ける
 * （`Organization.industries` / `technologies` が `String[]` で、会員ホームのマッチングが
 * それを前提にしているため）。Google フォーム側の単一選択・表記ゆれには合わせない。
 */
export const applicationSubmissionSchema = z.object({
    kind: applicationKindEnum,
    // 組織
    organizationName: z.string().trim().min(1, "組織名を入力してください").max(120),
    organizationDesc: z.string().trim().min(1, "組織概要を入力してください").max(ORG_DESC_MAX),
    // createUserSchema の homepageUrl と**同一**にする。`.url()` が無いと "https://" だけでも
    // 通ってしまい、承認してアカウントを作る段になって弾かれる。
    homepageUrl: z
        .string()
        .url("有効なURLを入力してください")
        .max(HOMEPAGE_MAX)
        // `.url()` だけだと javascript: / data: 等の危険スキームも通るため http(s) を明示要求する。
        .refine((v) => /^https?:\/\//.test(v), "http(s) で始まるURLを入力してください")
        .or(z.literal(""))
        .optional(),
    // 担当者
    name: z.string().trim().min(1, "担当者名を入力してください").max(60),
    department: optionalText(100),
    contactRole: optionalText(100),
    email: z.string().trim().email("有効なメールアドレスを入力してください").max(EMAIL_MAX),
    /** 申込者が希望するログインパスワード。サーバーでハッシュ化して保存する。 */
    password: passwordSchema,
    // industries は企業・会員の双方で使う（承認時に `Organization.industries` へ移る）。
    // 企業では「自社が属する業界」、会員では「得意とする業界」と意味が異なるだけで、格納先は同じ。
    industries: z.array(z.enum(INDUSTRY_OPTIONS)).max(INDUSTRY_OPTIONS.length).optional(),
    // technologies / referrer は会員申請のみ使う。企業申請では送られてこない。
    technologies: z.array(z.enum(TECHNOLOGY_OPTIONS)).max(TECHNOLOGY_OPTIONS.length).optional(),
    /*
     * referrer は企業・会員とも**必須**（紹介元が無ければ「なし」を選ぶ）。
     * 推薦者の人名（referrerName）は任意。
     * 必須化の判定は `findReferrerIssues` で行い、型の上では任意に留める。
     */
    referrer: z.enum(REFERRER_OPTIONS).optional(),
    referrerName: optionalText(REFERRER_NAME_MAX),
    // 企業申請のみ使う。企業名を会員に公開するかどうか。
    isNamePublic: z.boolean().optional(),
    /**
     * 企業申請のみ使う。ロゴアップロードエンドポイントが返した**保存パス**。
     *
     * 外部URLや任意の相対パスは受け付けない。未認証の相手が任意 URL を入れられると、
     * 承認時に組織のロゴとして引き継がれた瞬間に外部ビーコン（`<img src>`）になる。
     * また `startsWith("/")` だけの判定はプロトコル相対 URL（`//evil.example`）も通してしまう。
     *
     * prefix は公開配信される `logos/` ではなく `inquiry-logos/`。
     * 詳細は applications.controller.ts の INQUIRY_LOGO_DIR を参照。
     */
    organizationLogoPath: z
        .string()
        .regex(/^inquiry-logos\/[A-Za-z0-9._-]{1,128}$/, "ロゴの参照が不正です")
        .optional(),
    /**
     * 通知先メール。createUserSchema と同じ定義を共有する（件数・形式・長さの制約を揃える）。
     * 空欄行・重複・大小文字差はサーバー側で正規化して保存する。
     */
    notificationEmails: notificationEmailsSchema,
    note: optionalText(NOTE_MAX),
    /** 利用規約・プライバシーポリシーへの同意。false では受け付けない。 */
    agreedToTerms: z.boolean().refine((v) => v, {
        message: "利用規約とプライバシーポリシーへの同意が必要です",
    }),
    /**
     * ハニーポット。画面外に隠した入力欄で、人間は決して埋めない。
     * 埋まっていた場合は bot とみなしてサーバー側で黙って破棄する。
     */
    website: z.string().max(200).optional(),
});
/**
 * 対応担当の絞り込みで「未割当だけ」を表す予約値。
 *
 * 担当者は User ID で指定するが、「まだ誰も持っていないものを拾う」は運用上いちばん
 * 使う絞り込みなので、別のクエリパラメータを足さずに同じ `assignee` で表す。
 * ID は 16 文字以上（`ParseCuidPipe` の下限）なので、この語と衝突することはない。
 */
export const UNASSIGNED_ASSIGNEE = "unassigned";
/**
 * ADMIN 向け一覧のクエリ。
 * 管理画面はタブ = kind、絞り込み = status という構成なので、両方を任意で受ける。
 */
export const adminInquiryListQuerySchema = z.object({
    kind: inquiryKindEnum.optional(),
    status: inquiryStatusEnum.optional(),
    /**
     * 対応状況の複数指定（カンマ区切り）。管理画面は状態ごとにグループ分けして表示するので、
     * 「新着と確認中だけ見る」のように複数を一度に取る必要がある。
     * `status`（単一）と両方来た場合は**こちらを優先**する（応募一覧の `statuses` と同方針）。
     * 未知の値は黙って捨てる（URL を手で書き換えられても落とさない）。
     */
    statuses: z.string().max(200).optional(),
    /**
     * 氏名・組織名・メール・本文・件名の部分一致（大文字小文字を区別しない）。
     * 件名だけは列ではなく `payload.subject`（jsonb）を見る。
     */
    q: z.string().trim().max(200).optional(),
    /**
     * 対応担当の絞り込み。事務局（ADMIN）の User ID、または `unassigned`（未割当だけ）。
     *
     * **形は検証しない。** 存在しない ID は該当 0 件になるだけなので、
     * 手で書き換えられた URL に 400 を返して画面ごと壊す必要がない
     * （フロントの `validateSearch` も同じスキーマを使うため、ここで throw すると
     * 一覧が表示できなくなる）。上限は `ParseCuidPipe` が許す ID 長に合わせる。
     */
    assignee: z.string().trim().max(64).optional(),
    // 深いオフセットによるコストアンプを避けるため上限を設ける（application.ts と同方針）。
    page: z.coerce.number().int().min(1).max(10_000).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
/**
 * 取り込み共有鍵のパース。API 側の IngestKeyGuard と env 検証の両方から使い、
 * 「Guard が受理する鍵の集合」と「起動時に強度を検査する対象」を必ず一致させる。
 *
 * ローテーション中は新旧をカンマ区切りで併記できる。Key Vault の初期値
 * （実値投入前のプレースホルダー）はリポジトリに平文で存在する公開文字列なので、
 * 併記されていても有効な鍵として扱わないようここで除去する。
 */
export const KV_SECRET_PLACEHOLDER = "PLACEHOLDER_REPLACE_VIA_CLI_OR_CI";
export function parseIngestKeys(raw) {
    if (!raw)
        return [];
    return raw
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0 && s !== KV_SECRET_PLACEHOLDER);
}
